import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Save, Upload, Image as ImageIcon, Plus, Trash2, 
  Eye, RefreshCw, CheckCircle, AlertCircle 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomepageCMS = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState(null);
  const [activeTab, setActiveTab] = useState('hero');
  const [uploadedImages, setUploadedImages] = useState([]);

  useEffect(() => {
    loadContent();
    loadUploadedImages();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/homepage-cms`);
      
      // Response data is at root level, check for hero to determine if content exists
      if (response.data && response.data.hero) {
        // Merge with defaults to ensure new fields exist
        const defaults = getDefaultContent();
        const loadedContent = {
          ...defaults,
          ...response.data,
          dashboard_preview: response.data.dashboard_preview || defaults.dashboard_preview,
          benefits: response.data.benefits || defaults.benefits,
          trusted_stats: response.data.trusted_stats || defaults.trusted_stats,
          footer: response.data.footer || defaults.footer,
          cta: response.data.cta || defaults.cta
        };
        setContent(loadedContent);
      } else if (response.data.content) {
        const defaults = getDefaultContent();
        const loadedContent = {
          ...defaults,
          ...response.data.content,
          dashboard_preview: response.data.content.dashboard_preview || defaults.dashboard_preview,
          benefits: response.data.content.benefits || defaults.benefits,
          trusted_stats: response.data.content.trusted_stats || defaults.trusted_stats,
          footer: response.data.content.footer || defaults.footer,
          cta: response.data.content.cta || defaults.cta
        };
        setContent(loadedContent);
      } else {
        // Set default content
        setContent(getDefaultContent());
      }
      setLoading(false);
      toast.success('Content refreshed successfully!');
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Failed to load homepage content');
      setContent(getDefaultContent());
      setLoading(false);
    }
  };

  const loadUploadedImages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/homepage-cms/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUploadedImages(response.data.images || []);
    } catch (error) {
      console.error('Error loading images:', error);
    }
  };

  const getDefaultContent = () => ({
    hero: {
      title: 'Transform Your',
      subtitle: 'Healthcare Practice',
      description: 'The all-in-one membership management platform that helps healthcare providers grow revenue, save time, and deliver exceptional patient care.',
      cta_primary_text: 'Start Free 30-Day Trial',
      cta_primary_link: '/clinic-signup',
      cta_secondary_text: 'Watch Demo',
      cta_secondary_link: '#demo',
      hero_image_url: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&q=85',
      trust_badges: ['No credit card required', 'Setup in 5 minutes', 'Cancel anytime']
    },
    dashboard_preview: {
      revenue_badge: '+40%',
      active_members_count: '2,847',
      active_members_growth: '+23%',
      churn_accuracy: '92%',
      revenue_growth: '+40%',
      show_analytics_preview: true
    },
    features: [
      {
        icon: 'Users',
        title: 'Intelligent Patient CRM',
        description: 'Complete 360° patient view with medical history, preferences, and automated engagement tracking.',
        gradient: 'from-blue-500 to-cyan-500'
      }
    ],
    testimonials: [
      {
        quote: 'MyMedMember transformed our practice overnight. We\'ve grown our membership base by 300% in just 6 months!',
        author: 'Dr. Sarah Chen',
        role: 'Founder & Medical Director',
        clinic: 'Wellness Family Care',
        avatar: 'SC',
        rating: 5
      }
    ],
    stats: [
      { label: 'Healthcare Providers', value: '500+', icon: 'Users' },
      { label: 'Active Patients', value: '50K+', icon: 'Heart' },
      { label: 'Appointments Monthly', value: '1M+', icon: 'Calendar' },
      { label: 'Customer Satisfaction', value: '98%', icon: 'Star' }
    ],
    benefits: [
      { title: 'Increase Revenue by 40%', description: 'Convert more patients to members with streamlined onboarding and automated billing.', icon: 'TrendingUp' },
      { title: 'Save 15+ Hours Weekly', description: 'Automate administrative tasks and focus on what matters - patient care.', icon: 'Clock' },
      { title: 'Improve Patient Retention', description: 'Keep patients engaged with proactive communication and exceptional service.', icon: 'Heart' },
      { title: '100% HIPAA Compliant', description: 'Full audit logging, encryption, and role-based access control built-in.', icon: 'Shield' }
    ],
    trusted_by: {
      title: 'Trusted by Leading Healthcare Providers',
      subtitle: 'Join hundreds of practices transforming patient care',
      providers: [
        { name: "Valley Medical Center", specialty: "Family Medicine" },
        { name: "Sunrise Wellness Clinic", specialty: "Integrative Care" },
        { name: "Harbor Health Partners", specialty: "Primary Care" },
        { name: "Mountain View Family Care", specialty: "Pediatrics" },
        { name: "Coastal Medical Group", specialty: "Internal Medicine" },
        { name: "Riverside Health Associates", specialty: "Multi-Specialty" }
      ]
    },
    trusted_stats: [
      { label: 'Healthcare Providers', value: '500+', icon: 'Users' },
      { label: 'Active Patients', value: '50K+', icon: 'Heart' },
      { label: 'Appointments Monthly', value: '1M+', icon: 'Calendar' },
      { label: 'Customer Satisfaction', value: '98%', icon: 'Star' }
    ],
    footer: {
      company_name: 'Clinicax Systems',
      trademark_text: 'MyMedMember™ is a trademark of Clinicax Systems.',
      tagline: 'HIPAA Compliant Medical Membership Management',
      show_tagline: true,
      twitter_url: '',
      linkedin_url: '',
      facebook_url: ''
    },
    cta: {
      title: 'Ready to Transform Your Practice?',
      subtitle: 'Join 500+ healthcare providers growing with MyMedMember',
      primary_button_text: 'Start Your Free Trial',
      primary_button_link: '/register-clinic',
      secondary_button_text: 'Schedule a Demo',
      bottom_text: 'No credit card required • Setup in 5 minutes'
    }
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/homepage-cms`,
        content,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Homepage content saved successfully!');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error(error.response?.data?.detail || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/homepage-cms/upload-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('Logo uploaded successfully!');
      loadUploadedImages();
      return response.data.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
      return null;
    }
  };

  const updateHeroField = (field, value) => {
    setContent(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Homepage Content Management</h2>
          <p className="text-gray-600">Edit your homepage content, images, and text without touching code</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadContent}
            disabled={saving}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
        {['hero', 'dashboard-preview', 'benefits', 'features', 'testimonials', 'trusted-stats', 'trusted-by', 'cta', 'footer', 'images'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize whitespace-nowrap ${
              activeTab === tab
                ? 'border-b-2 border-teal-600 text-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab === 'dashboard-preview' ? 'Dashboard Preview' : tab === 'trusted-by' ? 'Trusted By' : tab === 'trusted-stats' ? 'Stats Banner' : tab === 'cta' ? 'CTA Section' : tab}
          </button>
        ))}
      </div>

      {/* Hero Section Editor */}
      {activeTab === 'hero' && content?.hero && (
        <Card>
          <CardHeader>
            <CardTitle>Hero Section</CardTitle>
            <CardDescription>Edit the main hero section of your homepage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Main Title</Label>
                <Input
                  value={content.hero.title}
                  onChange={(e) => updateHeroField('title', e.target.value)}
                  placeholder="Transform Your"
                />
              </div>
              <div className="space-y-2">
                <Label>Subtitle (Gradient Text)</Label>
                <Input
                  value={content.hero.subtitle}
                  onChange={(e) => updateHeroField('subtitle', e.target.value)}
                  placeholder="Healthcare Practice"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={content.hero.description}
                onChange={(e) => updateHeroField('description', e.target.value)}
                rows={3}
                placeholder="Your platform description..."
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Primary CTA Text</Label>
                <Input
                  value={content.hero.cta_primary_text}
                  onChange={(e) => updateHeroField('cta_primary_text', e.target.value)}
                  placeholder="Start Free Trial"
                />
              </div>
              <div className="space-y-2">
                <Label>Primary CTA Link</Label>
                <Input
                  value={content.hero.cta_primary_link}
                  onChange={(e) => updateHeroField('cta_primary_link', e.target.value)}
                  placeholder="/clinic-signup"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Secondary CTA Text</Label>
                <Input
                  value={content.hero.cta_secondary_text}
                  onChange={(e) => updateHeroField('cta_secondary_text', e.target.value)}
                  placeholder="Watch Demo"
                />
              </div>
              <div className="space-y-2">
                <Label>Secondary CTA Link</Label>
                <Input
                  value={content.hero.cta_secondary_link}
                  onChange={(e) => updateHeroField('cta_secondary_link', e.target.value)}
                  placeholder="#demo"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Hero Image URL</Label>
              <div className="flex gap-2">
                <Input
                  value={content.hero.hero_image_url}
                  onChange={(e) => updateHeroField('hero_image_url', e.target.value)}
                  placeholder="https://..."
                />
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('hero-image-upload').click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
                <input
                  id="hero-image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const url = await handleImageUpload(e);
                    if (url) updateHeroField('hero_image_url', url);
                  }}
                />
              </div>
              {content.hero.hero_image_url && (
                <img
                  src={content.hero.hero_image_url}
                  alt="Hero"
                  className="mt-2 w-full h-48 object-cover rounded-lg"
                />
              )}
            </div>

            <div className="space-y-2">
              <Label>Trust Badges (comma-separated)</Label>
              <Input
                value={content.hero.trust_badges?.join(', ')}
                onChange={(e) => updateHeroField('trust_badges', e.target.value.split(',').map(s => s.trim()))}
                placeholder="No credit card required, Setup in 5 minutes"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dashboard Preview Editor */}
      {activeTab === 'dashboard-preview' && (
        <Card>
          <CardHeader>
            <CardTitle>Dashboard Preview Stats</CardTitle>
            <CardDescription>Edit the statistics shown in the AI Analytics Dashboard preview on the homepage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-100">
              <p className="text-sm text-teal-800">
                💡 These values appear on the homepage dashboard preview image and help showcase your platform's capabilities to visitors.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Revenue Badge (top-right corner)</Label>
                <Input
                  value={content.dashboard_preview?.revenue_badge || '+40%'}
                  onChange={(e) => setContent(prev => ({
                    ...prev,
                    dashboard_preview: { ...prev.dashboard_preview, revenue_badge: e.target.value }
                  }))}
                  placeholder="+40%"
                />
                <p className="text-xs text-gray-500">Shown as "Revenue +X%" on the hero image</p>
              </div>
              <div className="space-y-2">
                <Label>Active Members Count</Label>
                <Input
                  value={content.dashboard_preview?.active_members_count || '2,847'}
                  onChange={(e) => setContent(prev => ({
                    ...prev,
                    dashboard_preview: { ...prev.dashboard_preview, active_members_count: e.target.value }
                  }))}
                  placeholder="2,847"
                />
                <p className="text-xs text-gray-500">Total active members number shown in dashboard</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Active Members Growth %</Label>
                <Input
                  value={content.dashboard_preview?.active_members_growth || '+23%'}
                  onChange={(e) => setContent(prev => ({
                    ...prev,
                    dashboard_preview: { ...prev.dashboard_preview, active_members_growth: e.target.value }
                  }))}
                  placeholder="+23%"
                />
                <p className="text-xs text-gray-500">Growth percentage shown next to active members</p>
              </div>
              <div className="space-y-2">
                <Label>Churn Accuracy %</Label>
                <Input
                  value={content.dashboard_preview?.churn_accuracy || '92%'}
                  onChange={(e) => setContent(prev => ({
                    ...prev,
                    dashboard_preview: { ...prev.dashboard_preview, churn_accuracy: e.target.value }
                  }))}
                  placeholder="92%"
                />
                <p className="text-xs text-gray-500">AI churn prediction accuracy displayed</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Revenue Growth %</Label>
                <Input
                  value={content.dashboard_preview?.revenue_growth || '+40%'}
                  onChange={(e) => setContent(prev => ({
                    ...prev,
                    dashboard_preview: { ...prev.dashboard_preview, revenue_growth: e.target.value }
                  }))}
                  placeholder="+40%"
                />
                <p className="text-xs text-gray-500">Revenue growth indicator in dashboard</p>
              </div>
              <div className="space-y-2 flex items-center gap-3 pt-6">
                <input
                  type="checkbox"
                  id="show_analytics"
                  checked={content.dashboard_preview?.show_analytics_preview !== false}
                  onChange={(e) => setContent(prev => ({
                    ...prev,
                    dashboard_preview: { ...prev.dashboard_preview, show_analytics_preview: e.target.checked }
                  }))}
                  className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <Label htmlFor="show_analytics" className="cursor-pointer">Show Analytics Preview on Homepage</Label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Benefits Editor */}
      {activeTab === 'benefits' && (
        <Card>
          <CardHeader>
            <CardTitle>Benefits Section</CardTitle>
            <CardDescription>Edit the key benefits shown on the homepage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {(content.benefits || [
              { title: 'Increase Revenue by 40%', description: 'Convert more patients to members with streamlined onboarding and automated billing.', icon: 'TrendingUp' },
              { title: 'Save 15+ Hours Weekly', description: 'Automate administrative tasks and focus on what matters - patient care.', icon: 'Clock' },
              { title: 'Improve Patient Retention', description: 'Keep patients engaged with proactive communication and exceptional service.', icon: 'Heart' },
              { title: '100% HIPAA Compliant', description: 'Full audit logging, encryption, and role-based access control built-in.', icon: 'Shield' }
            ]).map((benefit, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-900">Benefit {index + 1}</h4>
                  <select
                    value={benefit.icon}
                    onChange={(e) => {
                      const newBenefits = [...(content.benefits || [])];
                      newBenefits[index] = { ...benefit, icon: e.target.value };
                      setContent(prev => ({ ...prev, benefits: newBenefits }));
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="TrendingUp">📈 TrendingUp</option>
                    <option value="Clock">🕐 Clock</option>
                    <option value="Heart">❤️ Heart</option>
                    <option value="Shield">🛡️ Shield</option>
                    <option value="Users">👥 Users</option>
                    <option value="Star">⭐ Star</option>
                    <option value="Calendar">📅 Calendar</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={benefit.title}
                    onChange={(e) => {
                      const newBenefits = [...(content.benefits || [])];
                      newBenefits[index] = { ...benefit, title: e.target.value };
                      setContent(prev => ({ ...prev, benefits: newBenefits }));
                    }}
                    placeholder="Benefit title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={benefit.description}
                    onChange={(e) => {
                      const newBenefits = [...(content.benefits || [])];
                      newBenefits[index] = { ...benefit, description: e.target.value };
                      setContent(prev => ({ ...prev, benefits: newBenefits }));
                    }}
                    rows={2}
                    placeholder="Benefit description"
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Images Library */}
      {activeTab === 'images' && (
        <Card>
          <CardHeader>
            <CardTitle>Image Library</CardTitle>
            <CardDescription>Manage uploaded images for your homepage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Button
                onClick={() => document.getElementById('general-image-upload').click()}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload New Image
              </Button>
              <input
                id="general-image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </div>

            {uploadedImages.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No images uploaded yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {uploadedImages.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image.url}
                      alt={image.filename}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-white hover:text-white hover:bg-white/20"
                        onClick={() => {
                          navigator.clipboard.writeText(image.url);
                          toast.success('URL copied to clipboard!');
                        }}
                      >
                        Copy URL
                      </Button>
                    </div>
                    <p className="text-xs text-gray-600 mt-1 truncate">{image.filename}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Features Section Editor */}
      {activeTab === 'features' && content?.features && (
        <Card>
          <CardHeader>
            <CardTitle>Features Section</CardTitle>
            <CardDescription>Manage the features displayed on your homepage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {content.features.map((feature, index) => (
              <Card key={index} className="border-2 border-gray-200">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <h4 className="font-semibold text-lg">Feature {index + 1}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newFeatures = content.features.filter((_, i) => i !== index);
                        setContent({ ...content, features: newFeatures });
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Icon Name</Label>
                      <Input
                        value={feature.icon}
                        onChange={(e) => {
                          const newFeatures = [...content.features];
                          newFeatures[index].icon = e.target.value;
                          setContent({ ...content, features: newFeatures });
                        }}
                        placeholder="Users, Heart, Calendar, etc."
                      />
                      <p className="text-xs text-gray-500">Available: Users, Heart, Calendar, Star, Shield, Award, BarChart3, MessageSquare, Zap</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Gradient</Label>
                      <Input
                        value={feature.gradient}
                        onChange={(e) => {
                          const newFeatures = [...content.features];
                          newFeatures[index].gradient = e.target.value;
                          setContent({ ...content, features: newFeatures });
                        }}
                        placeholder="from-blue-500 to-cyan-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={feature.title}
                      onChange={(e) => {
                        const newFeatures = [...content.features];
                        newFeatures[index].title = e.target.value;
                        setContent({ ...content, features: newFeatures });
                      }}
                      placeholder="Feature title"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={feature.description}
                      onChange={(e) => {
                        const newFeatures = [...content.features];
                        newFeatures[index].description = e.target.value;
                        setContent({ ...content, features: newFeatures });
                      }}
                      rows={3}
                      placeholder="Feature description"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}

            <Button
              onClick={() => {
                const newFeature = {
                  icon: 'Users',
                  title: 'New Feature',
                  description: 'Feature description goes here',
                  gradient: 'from-blue-500 to-cyan-500'
                };
                setContent({ ...content, features: [...content.features, newFeature] });
              }}
              className="w-full border-2 border-dashed border-gray-300 hover:border-teal-500 bg-transparent hover:bg-teal-50 text-gray-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Feature
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Testimonials Section Editor */}
      {activeTab === 'testimonials' && content?.testimonials && (
        <Card>
          <CardHeader>
            <CardTitle>Testimonials Section</CardTitle>
            <CardDescription>Manage customer testimonials displayed on your homepage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {content.testimonials.map((testimonial, index) => (
              <Card key={index} className="border-2 border-gray-200">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <h4 className="font-semibold text-lg">Testimonial {index + 1}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newTestimonials = content.testimonials.filter((_, i) => i !== index);
                        setContent({ ...content, testimonials: newTestimonials });
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Quote</Label>
                    <Textarea
                      value={testimonial.quote}
                      onChange={(e) => {
                        const newTestimonials = [...content.testimonials];
                        newTestimonials[index].quote = e.target.value;
                        setContent({ ...content, testimonials: newTestimonials });
                      }}
                      rows={3}
                      placeholder="Customer testimonial quote..."
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Author Name</Label>
                      <Input
                        value={testimonial.author}
                        onChange={(e) => {
                          const newTestimonials = [...content.testimonials];
                          newTestimonials[index].author = e.target.value;
                          setContent({ ...content, testimonials: newTestimonials });
                        }}
                        placeholder="Dr. John Smith"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Role/Title</Label>
                      <Input
                        value={testimonial.role}
                        onChange={(e) => {
                          const newTestimonials = [...content.testimonials];
                          newTestimonials[index].role = e.target.value;
                          setContent({ ...content, testimonials: newTestimonials });
                        }}
                        placeholder="Medical Director"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Clinic/Company</Label>
                      <Input
                        value={testimonial.clinic}
                        onChange={(e) => {
                          const newTestimonials = [...content.testimonials];
                          newTestimonials[index].clinic = e.target.value;
                          setContent({ ...content, testimonials: newTestimonials });
                        }}
                        placeholder="Wellness Clinic"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Avatar (Initials)</Label>
                      <Input
                        value={testimonial.avatar}
                        onChange={(e) => {
                          const newTestimonials = [...content.testimonials];
                          newTestimonials[index].avatar = e.target.value;
                          setContent({ ...content, testimonials: newTestimonials });
                        }}
                        placeholder="JS"
                        maxLength={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Rating (1-5)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="5"
                        value={testimonial.rating}
                        onChange={(e) => {
                          const newTestimonials = [...content.testimonials];
                          newTestimonials[index].rating = parseInt(e.target.value);
                          setContent({ ...content, testimonials: newTestimonials });
                        }}
                        placeholder="5"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Button
              onClick={() => {
                const newTestimonial = {
                  quote: 'This platform transformed our practice!',
                  author: 'Dr. Jane Doe',
                  role: 'Practice Owner',
                  clinic: 'Health Clinic',
                  avatar: 'JD',
                  rating: 5
                };
                setContent({ ...content, testimonials: [...content.testimonials, newTestimonial] });
              }}
              className="w-full border-2 border-dashed border-gray-300 hover:border-teal-500 bg-transparent hover:bg-teal-50 text-gray-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Testimonial
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats Banner Section Editor */}
      {activeTab === 'trusted-stats' && content?.trusted_stats && (
        <Card>
          <CardHeader>
            <CardTitle>Stats Banner Section</CardTitle>
            <CardDescription>Manage the statistics displayed below the hero section (e.g., "500+ Healthcare Providers")</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {content.trusted_stats.map((stat, index) => (
              <Card key={index} className="border-2 border-gray-200">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="font-semibold text-lg">Stat {index + 1}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newStats = content.trusted_stats.filter((_, i) => i !== index);
                        setContent({ ...content, trusted_stats: newStats });
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Value</Label>
                      <Input
                        value={stat.value}
                        onChange={(e) => {
                          const newStats = [...content.trusted_stats];
                          newStats[index].value = e.target.value;
                          setContent({ ...content, trusted_stats: newStats });
                        }}
                        placeholder="500+"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Label</Label>
                      <Input
                        value={stat.label}
                        onChange={(e) => {
                          const newStats = [...content.trusted_stats];
                          newStats[index].label = e.target.value;
                          setContent({ ...content, trusted_stats: newStats });
                        }}
                        placeholder="Healthcare Providers"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Icon</Label>
                      <Input
                        value={stat.icon}
                        onChange={(e) => {
                          const newStats = [...content.trusted_stats];
                          newStats[index].icon = e.target.value;
                          setContent({ ...content, trusted_stats: newStats });
                        }}
                        placeholder="Users, Heart, Calendar, Star"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Button
              onClick={() => {
                const newStat = {
                  label: 'New Stat',
                  value: '100+',
                  icon: 'Star'
                };
                setContent({ ...content, trusted_stats: [...content.trusted_stats, newStat] });
              }}
              className="w-full border-2 border-dashed border-gray-300 hover:border-teal-500 bg-transparent hover:bg-teal-50 text-gray-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Stat
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Trusted By Section Editor */}
      {activeTab === 'trusted-by' && (
        <Card>
          <CardHeader>
            <CardTitle>Trusted By Section</CardTitle>
            <CardDescription>Edit the "Trusted by Leading Healthcare Providers" section</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Section Title</Label>
                <Input
                  value={content?.trusted_by?.title || ''}
                  onChange={(e) => {
                    setContent({
                      ...content,
                      trusted_by: {
                        ...content.trusted_by,
                        title: e.target.value,
                        subtitle: content.trusted_by?.subtitle || '',
                        providers: content.trusted_by?.providers || []
                      }
                    });
                  }}
                  placeholder="Trusted by Leading Healthcare Providers"
                />
              </div>
              <div className="space-y-2">
                <Label>Section Subtitle</Label>
                <Input
                  value={content?.trusted_by?.subtitle || ''}
                  onChange={(e) => {
                    setContent({
                      ...content,
                      trusted_by: {
                        ...content.trusted_by,
                        title: content.trusted_by?.title || '',
                        subtitle: e.target.value,
                        providers: content.trusted_by?.providers || []
                      }
                    });
                  }}
                  placeholder="Join hundreds of practices transforming patient care"
                />
              </div>
            </div>

            <div className="space-y-4 mt-8">
              <h4 className="font-semibold text-lg">Healthcare Providers</h4>
              {(content?.trusted_by?.providers || []).map((provider, index) => (
                <Card key={index} className="border-2 border-gray-200">
                  <CardContent className="pt-6">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-semibold">Provider {index + 1}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newProviders = (content.trusted_by?.providers || []).filter((_, i) => i !== index);
                          setContent({
                            ...content,
                            trusted_by: {
                              ...content.trusted_by,
                              providers: newProviders
                            }
                          });
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Provider Name</Label>
                          <Input
                            value={provider.name}
                            onChange={(e) => {
                              const newProviders = [...(content.trusted_by?.providers || [])];
                              newProviders[index].name = e.target.value;
                              setContent({
                                ...content,
                                trusted_by: {
                                  ...content.trusted_by,
                                  providers: newProviders
                                }
                              });
                            }}
                            placeholder="Valley Medical Center"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Specialty</Label>
                          <Input
                            value={provider.specialty}
                            onChange={(e) => {
                              const newProviders = [...(content.trusted_by?.providers || [])];
                              newProviders[index].specialty = e.target.value;
                              setContent({
                                ...content,
                                trusted_by: {
                                  ...content.trusted_by,
                                  providers: newProviders
                                }
                              });
                            }}
                            placeholder="Family Medicine"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Logo URL (Optional)</Label>
                        <div className="flex gap-2">
                          <Input
                            value={provider.logo_url || ''}
                            onChange={(e) => {
                              const newProviders = [...(content.trusted_by?.providers || [])];
                              newProviders[index].logo_url = e.target.value;
                              setContent({
                                ...content,
                                trusted_by: {
                                  ...content.trusted_by,
                                  providers: newProviders
                                }
                              });
                            }}
                            placeholder="https://example.com/logo.png or /uploads/logo.png"
                          />
                          <input
                            id={`logo-upload-${index}`}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                // Validate file size (max 5MB)
                                if (file.size > 5 * 1024 * 1024) {
                                  toast.error('File too large. Maximum size is 5MB');
                                  return;
                                }
                                
                                // Show uploading toast
                                toast.info('Uploading logo...');
                                
                                const url = await handleImageUpload(file);
                                if (url) {
                                  const newProviders = [...(content.trusted_by?.providers || [])];
                                  newProviders[index].logo_url = url;
                                  setContent({
                                    ...content,
                                    trusted_by: {
                                      ...content.trusted_by,
                                      providers: newProviders
                                    }
                                  });
                                }
                                // Reset input
                                e.target.value = '';
                              }
                            }}
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm" 
                            className="whitespace-nowrap"
                            onClick={() => {
                              document.getElementById(`logo-upload-${index}`)?.click();
                            }}
                          >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload Logo
                          </Button>
                        </div>
                        {provider.logo_url && (
                          <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-3">
                              <img
                                src={provider.logo_url}
                                alt={provider.name}
                                className="w-16 h-16 object-contain bg-white rounded border border-gray-200 p-1"
                              />
                              <div className="flex-1">
                                <p className="text-xs text-gray-600 font-medium mb-1">Preview</p>
                                <p className="text-xs text-gray-500 truncate">{provider.logo_url}</p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newProviders = [...(content.trusted_by?.providers || [])];
                                  newProviders[index].logo_url = null;
                                  setContent({
                                    ...content,
                                    trusted_by: {
                                      ...content.trusted_by,
                                      providers: newProviders
                                    }
                                  });
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-gray-500">
                          💡 Tip: Use transparent PNG logos (recommended size: 400x200px)
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <Button
                onClick={() => {
                  const newProvider = {
                    name: 'New Healthcare Provider',
                    specialty: 'Primary Care',
                    logo_url: null
                  };
                  setContent({
                    ...content,
                    trusted_by: {
                      title: content.trusted_by?.title || 'Trusted by Leading Healthcare Providers',
                      subtitle: content.trusted_by?.subtitle || 'Join hundreds of practices transforming patient care',
                      providers: [...(content.trusted_by?.providers || []), newProvider]
                    }
                  });
                }}
                className="w-full border-2 border-dashed border-gray-300 hover:border-teal-500 bg-transparent hover:bg-teal-50 text-gray-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Provider
              </Button>
              
              <div className="mt-6 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-teal-900 mb-1">Beautiful Logo Slider</h4>
                    <p className="text-sm text-teal-800 mb-2">
                      Your providers will be displayed in an elegant auto-scrolling slider with smooth animations.
                    </p>
                    <ul className="text-xs text-teal-700 space-y-1">
                      <li>✨ Auto-scrolling animation (pauses on hover)</li>
                      <li>🎨 Logos display in grayscale, colorize on hover</li>
                      <li>📱 Fully responsive design</li>
                      <li>⚡ Smooth transitions and modern effects</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CTA Section Editor */}
      {activeTab === 'cta' && (
        <Card>
          <CardHeader>
            <CardTitle>Call-to-Action Section</CardTitle>
            <CardDescription>Edit the "Ready to Transform Your Practice?" section below pricing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Section Title</Label>
              <Input
                value={content?.cta?.title || 'Ready to Transform Your Practice?'}
                onChange={(e) => {
                  setContent({
                    ...content,
                    cta: {
                      ...content.cta,
                      title: e.target.value
                    }
                  });
                }}
                placeholder="Ready to Transform Your Practice?"
              />
            </div>

            <div className="space-y-2">
              <Label>Subtitle</Label>
              <Input
                value={content?.cta?.subtitle || 'Join 500+ healthcare providers growing with MyMedMember'}
                onChange={(e) => {
                  setContent({
                    ...content,
                    cta: {
                      ...content.cta,
                      subtitle: e.target.value
                    }
                  });
                }}
                placeholder="Join 500+ healthcare providers growing with MyMedMember"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Button Text</Label>
                <Input
                  value={content?.cta?.primary_button_text || 'Start Your Free Trial'}
                  onChange={(e) => {
                    setContent({
                      ...content,
                      cta: {
                        ...content.cta,
                        primary_button_text: e.target.value
                      }
                    });
                  }}
                  placeholder="Start Your Free Trial"
                />
              </div>
              <div className="space-y-2">
                <Label>Primary Button Link</Label>
                <Input
                  value={content?.cta?.primary_button_link || '/register-clinic'}
                  onChange={(e) => {
                    setContent({
                      ...content,
                      cta: {
                        ...content.cta,
                        primary_button_link: e.target.value
                      }
                    });
                  }}
                  placeholder="/register-clinic"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Secondary Button Text</Label>
              <Input
                value={content?.cta?.secondary_button_text || 'Schedule a Demo'}
                onChange={(e) => {
                  setContent({
                    ...content,
                    cta: {
                      ...content.cta,
                      secondary_button_text: e.target.value
                    }
                  });
                }}
                placeholder="Schedule a Demo"
              />
            </div>

            <div className="space-y-2">
              <Label>Bottom Text</Label>
              <Input
                value={content?.cta?.bottom_text || 'No credit card required • Setup in 5 minutes'}
                onChange={(e) => {
                  setContent({
                    ...content,
                    cta: {
                      ...content.cta,
                      bottom_text: e.target.value
                    }
                  });
                }}
                placeholder="No credit card required • Setup in 5 minutes"
              />
            </div>

            {/* Preview */}
            <div className="mt-6 p-8 bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 rounded-lg text-center">
              <p className="text-sm text-white/70 mb-2">Preview:</p>
              <h3 className="text-2xl font-bold text-white mb-2">
                {content?.cta?.title || 'Ready to Transform Your Practice?'}
              </h3>
              <p className="text-white/90 mb-4">
                {content?.cta?.subtitle || 'Join 500+ healthcare providers growing with MyMedMember'}
              </p>
              <div className="flex justify-center gap-3 mb-3">
                <span className="px-4 py-2 bg-white text-teal-600 rounded-lg text-sm font-medium">
                  {content?.cta?.primary_button_text || 'Start Your Free Trial'}
                </span>
                <span className="px-4 py-2 border border-white text-white rounded-lg text-sm font-medium">
                  {content?.cta?.secondary_button_text || 'Schedule a Demo'}
                </span>
              </div>
              <p className="text-white/70 text-sm">
                {content?.cta?.bottom_text || 'No credit card required • Setup in 5 minutes'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Footer Section Editor */}
      {activeTab === 'footer' && (
        <Card>
          <CardHeader>
            <CardTitle>Footer Settings</CardTitle>
            <CardDescription>Manage the copyright and trademark information displayed in the footer</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={content?.footer?.company_name || 'Clinicax Systems'}
                onChange={(e) => {
                  setContent({
                    ...content,
                    footer: {
                      ...content.footer,
                      company_name: e.target.value
                    }
                  });
                }}
                placeholder="Clinicax Systems"
              />
              <p className="text-xs text-gray-500">This will appear as: © {new Date().getFullYear()} [Company Name]. All rights reserved.</p>
            </div>

            <div className="space-y-2">
              <Label>Trademark Text</Label>
              <Input
                value={content?.footer?.trademark_text || 'MyMedMember™ is a trademark of Clinicax Systems.'}
                onChange={(e) => {
                  setContent({
                    ...content,
                    footer: {
                      ...content.footer,
                      trademark_text: e.target.value
                    }
                  });
                }}
                placeholder="MyMedMember™ is a trademark of Clinicax Systems."
              />
              <p className="text-xs text-gray-500">Use ™ for unregistered trademark or ® for registered trademark</p>
            </div>

            <div className="space-y-2">
              <Label>Tagline</Label>
              <Input
                value={content?.footer?.tagline || 'HIPAA Compliant Medical Membership Management'}
                onChange={(e) => {
                  setContent({
                    ...content,
                    footer: {
                      ...content.footer,
                      tagline: e.target.value
                    }
                  });
                }}
                placeholder="HIPAA Compliant Medical Membership Management"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="show_tagline"
                checked={content?.footer?.show_tagline !== false}
                onChange={(e) => {
                  setContent({
                    ...content,
                    footer: {
                      ...content.footer,
                      show_tagline: e.target.checked
                    }
                  });
                }}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <Label htmlFor="show_tagline" className="text-sm font-normal cursor-pointer">
                Show tagline in footer
              </Label>
            </div>

            {/* Social Media Links */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h4 className="font-medium text-gray-900 mb-4">Social Media Links</h4>
              <p className="text-xs text-gray-500 mb-4">Leave empty to hide social icons from the footer</p>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Twitter/X URL</Label>
                  <Input
                    value={content?.footer?.twitter_url || ''}
                    onChange={(e) => {
                      setContent({
                        ...content,
                        footer: {
                          ...content.footer,
                          twitter_url: e.target.value
                        }
                      });
                    }}
                    placeholder="https://twitter.com/yourhandle"
                  />
                </div>
                <div className="space-y-2">
                  <Label>LinkedIn URL</Label>
                  <Input
                    value={content?.footer?.linkedin_url || ''}
                    onChange={(e) => {
                      setContent({
                        ...content,
                        footer: {
                          ...content.footer,
                          linkedin_url: e.target.value
                        }
                      });
                    }}
                    placeholder="https://linkedin.com/company/yourcompany"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Facebook URL</Label>
                  <Input
                    value={content?.footer?.facebook_url || ''}
                    onChange={(e) => {
                      setContent({
                        ...content,
                        footer: {
                          ...content.footer,
                          facebook_url: e.target.value
                        }
                      });
                    }}
                    placeholder="https://facebook.com/yourpage"
                  />
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="mt-6 p-6 bg-gray-900 rounded-lg">
              <p className="text-sm text-gray-300 mb-1">Preview:</p>
              <div className="text-gray-400 text-sm">
                <p>© {new Date().getFullYear()} {content?.footer?.company_name || 'Clinicax Systems'}. All rights reserved.</p>
                <p className="text-gray-500 text-xs mt-1">{content?.footer?.trademark_text || 'MyMedMember™ is a trademark of Clinicax Systems.'}</p>
                {(content?.footer?.show_tagline !== false) && content?.footer?.tagline && (
                  <p className="text-gray-500 text-xs mt-1">{content?.footer?.tagline}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HomepageCMS;
